import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { CATEGORIES } from "@/lib/constants";

export default function CreatePost() {
    const [f, setF] = useState({
        category: "builds",
        title: "",
        body: "",
        photos: [],
        vehicle_id: "",
    });
    const [myVehicles, setMyVehicles] = useState([]);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/garage/vehicles");
                setMyVehicles(data);
            } catch {}
        })();
    }, []);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f, vehicle_id: f.vehicle_id || null };
            const { data } = await api.post("/posts", payload);
            navigate(`/community/${data.post_id}`);
            toast.success("Post published");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-onyx-900">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg">Create Post</div>
                </div>
            </div>

            <form onSubmit={submit} className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <div className="surface rounded-md p-5 space-y-3">
                    <label className="flex flex-col gap-1.5">
                        <span className="label-caps">Category</span>
                        <select
                            data-testid="post-category"
                            value={f.category}
                            onChange={(e) => setF({ ...f, category: e.target.value })}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1.5">
                        <span className="label-caps">Title</span>
                        <input
                            data-testid="post-title"
                            required
                            maxLength={180}
                            value={f.title}
                            onChange={(e) => setF({ ...f, title: e.target.value })}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat"
                            placeholder="Finally hit 830whp on my Redeye"
                        />
                    </label>

                    <label className="flex flex-col gap-1.5">
                        <span className="label-caps">Body</span>
                        <textarea
                            data-testid="post-body"
                            value={f.body}
                            onChange={(e) => setF({ ...f, body: e.target.value })}
                            className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat h-40"
                            placeholder="Share the build, ask a question, or start a discussion..."
                        />
                    </label>

                    {myVehicles.length > 0 && (
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Tag a vehicle (optional)</span>
                            <select
                                data-testid="post-vehicle"
                                value={f.vehicle_id}
                                onChange={(e) => setF({ ...f, vehicle_id: e.target.value })}
                                className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat"
                            >
                                <option value="">None</option>
                                {myVehicles.map((v) => (
                                    <option key={v.vehicle_id} value={v.vehicle_id}>
                                        {v.year} {v.model_name} {v.trim}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>

                <div className="surface rounded-md p-5">
                    <div className="mb-3">
                        <div className="headline text-lg">Photos</div>
                        <div className="text-xs text-onyx-500 mt-1">Optional. Up to 10 photos.</div>
                    </div>
                    <ImageUploader value={f.photos} onChange={(v) => setF({ ...f, photos: v })} max={10} />
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="post-submit"
                    className="btn-red w-full py-3.5 rounded-sm disabled:opacity-60"
                >
                    {busy ? "Publishing..." : "Publish"}
                </button>
            </form>
        </div>
    );
}
